<template>
  <div>
    <!-- Overlay -->
    <transition name="overlay-fade">
      <div
        v-if="isOpen"
        class="notification-overlay"
        @click="closePanel"
      ></div>
    </transition>

    <!-- Side Panel -->
    <transition name="panel-slide">
      <div v-if="isOpen" class="notification-sidepanel">
        <!-- Header -->
        <div class="panel-header">
          <div class="panel-title">
            <i class="el-icon-bell"></i>
            {{ $t('mensagens.notificacoes.titulo') }}
            <el-badge
              v-if="unreadCount > 0"
              :value="unreadCount"
              :max="99"
              class="unread-badge"
            />
          </div>
          <div class="panel-actions">
            <el-button
              v-if="notifications.length > 0"
              type="text"
              size="small"
              @click="markAllAsRead"
            >
              {{ $t('mensagens.notificacoes.marcarTodasLidas') }}
            </el-button>
            <el-button
              v-if="notifications.length > 0"
              type="text"
              size="small"
              @click="confirmClearAll"
            >
              {{ $t('mensagens.notificacoes.limparTudo') }}
            </el-button>
            <button class="close-button" @click="closePanel">
              <i class="el-icon-close"></i>
            </button>
          </div>
        </div>

        <!-- Modal de Confirmação de Limpeza -->
        <transition name="fade">
          <div v-if="showClearConfirmation" class="clear-confirmation">
            <div class="confirmation-content">
              <i class="el-icon-warning"></i>
              <p class="confirmation-title">{{ $t('mensagens.notificacoes.confirmarLimpezaTitulo') }}</p>
              <p class="confirmation-message">{{ $t('mensagens.notificacoes.confirmarLimpezaMensagem') }}</p>
              <div class="confirmation-actions">
                <el-button size="small" @click="cancelClearAll">
                  {{ $t('mensagens.notificacoes.cancelar') }}
                </el-button>
                <el-button type="danger" size="small" @click="executeClearAll">
                  {{ $t('mensagens.notificacoes.confirmarLimparTudo') }}
                </el-button>
              </div>
            </div>
          </div>
        </transition>

        <!-- Filtros -->
        <div v-if="categories.length > 0" class="panel-filters">
          <el-button
            :type="selectedCategory === null ? 'primary' : ''"
            size="small"
            @click="selectedCategory = null"
          >
            {{ $t('mensagens.notificacoes.todas') }} ({{ notifications.length }})
          </el-button>
          <el-button
            v-for="category in categories"
            :key="category"
            :type="selectedCategory === category ? 'primary' : ''"
            size="small"
            @click="selectedCategory = category"
          >
            {{ category }} ({{ getCategoryCount(category) }})
          </el-button>
        </div>

        <!-- Lista de Notificações -->
        <div class="panel-content">
          <div v-if="filteredNotifications.length === 0" class="empty-state">
            <i class="el-icon-bell"></i>
            <p>{{ $t('mensagens.notificacoes.vazio') }}</p>
          </div>

          <div
            v-for="notification in filteredNotifications"
            :key="notification.id"
            :class="['notification-item', { 'unread': !notification.read }]"
            @click="handleNotificationClick(notification)"
          >
            <!-- Indicador de não lida -->
            <div v-if="!notification.read" class="unread-indicator"></div>

            <!-- Ícone -->
            <div :class="['notification-icon', `icon-${getNotificationVariant(notification.status)}`]">
              <i :class="getNotificationIcon(notification.status)"></i>
            </div>

            <!-- Conteúdo -->
            <div class="notification-content">
              <div class="notification-header">
                <span class="notification-title">{{ notification.title }}</span>
                <span class="notification-time">{{ formatTime(notification.timestamp) }}</span>
              </div>
              <div class="notification-message">{{ notification.message }}</div>

              <!-- Metadata (arquivo, etc) -->
              <div v-if="notification.metadata && notification.metadata.fileName" class="notification-metadata">
                <i class="el-icon-document"></i>
                {{ notification.metadata.fileName }}
              </div>

              <!-- Categoria -->
              <div v-if="notification.category" class="notification-category">
                <el-tag size="mini" type="info">{{ notification.category }}</el-tag>
              </div>

              <!-- Ações -->
              <div v-if="notification.actions && notification.actions.length > 0" class="notification-actions">
                <el-button
                  v-for="(action, index) in notification.actions"
                  :key="index"
                  :type="action.type || 'primary'"
                  size="small"
                  :icon="action.icon"
                  @click.stop="handleAction(action, notification)"
                >
                  {{ action.label }}
                </el-button>
              </div>
            </div>

            <!-- Botão de remover -->
            <button class="remove-button" @click.stop="removeNotification(notification.id)">
              <i class="el-icon-close"></i>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import NotificationService from '@/services/NotificationService';
import { Message } from 'elementui-lts';

export default {
  name: 'NotificationSidePanel',
  data() {
    return {
      isOpen: false,
      notifications: [],
      selectedCategory: null,
      highlightedNotificationId: null,
      showClearConfirmation: false
    };
  },
  computed: {
    unreadCount() {
      return this.notifications.filter(n => !n.read).length;
    },
    categories() {
      const cats = new Set();
      this.notifications.forEach(n => {
        if (n.category) cats.add(n.category);
      });
      return Array.from(cats);
    },
    filteredNotifications() {
      if (this.selectedCategory === null) {
        return this.notifications;
      }
      return this.notifications.filter(n => n.category === this.selectedCategory);
    }
  },
  mounted() {
    // Carrega notificações do localStorage
    this.loadNotifications();

    // Escuta eventos globais
    this.$root.$on('open-notification-panel', this.openPanel);
    this.$root.$on('close-notification-panel', this.closePanel);
    this.$root.$on('toggle-notification-panel', this.togglePanel);

    // Escuta mudanças no NotificationService
    NotificationService.addListener(this.onNotificationUpdate);

    // Atalho de teclado ESC para fechar
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    this.$root.$off('open-notification-panel', this.openPanel);
    this.$root.$off('close-notification-panel', this.closePanel);
    this.$root.$off('toggle-notification-panel', this.togglePanel);
    NotificationService.removeListener(this.onNotificationUpdate);
    document.addEventListener('keydown', this.handleKeydown);
  },
  methods: {
    loadNotifications() {
      try {
        const stored = localStorage.getItem('systemNotifications');
        if (stored) {
          this.notifications = JSON.parse(stored);
          // Reconstruir ações com handlers (não podem ser serializados)
          this.notifications.forEach(notification => {
            if (notification.type === 'REPORT' && notification.status === 'PROCESSADO' && notification.metadata) {
              notification.actions = this.createReportActions(notification);
            }
          });
        }
      } catch (error) {
        this.notifications = [];
      }
    },

    createReportActions(notification) {
      const actions = [];

      if (notification.metadata.nomeArquivo) {
        actions.push({
          label: this.$t('mensagens.notificacoes.baixar'),
          icon: 'el-icon-download',
          type: 'primary',
          handler: async (vm, notif) => {
            try {

              // Importar o serviço dinamicamente
              const ArquivoFilaProcessamentoService = require('@/services/ArquivoFilaProcessamentoService').default;
              await ArquivoFilaProcessamentoService.downloadArquivo(notification.metadata.nomeArquivo);

              Message.success('Download iniciado!');

              // Remover notificação após download
              setTimeout(() => {
                vm.removeNotification(notification.id);
              }, 2000);

            } catch (error) {
              Message.error('Erro ao fazer download do arquivo');
            }
          }
        });
      }

      return actions;
    },

    saveNotifications() {
      try {
        const notificationsToSave = this.notifications.map(n => ({
          ...n,
          // Remover handlers para não tentar serializar funções
          actions: n.actions ? n.actions.map(a => ({
            label: a.label,
            icon: a.icon,
            type: a.type
            // handler não é salvo
          })) : []
        }));

        localStorage.setItem('systemNotifications', JSON.stringify(notificationsToSave));
      } catch (error) {
      }
    },

    onNotificationUpdate() {
      this.loadNotifications();
    },

    openPanel(notificationId = null) {
      this.isOpen = true;
      this.highlightedNotificationId = notificationId;

      // Marca notificações como lidas após 1 segundo
      setTimeout(() => {
        this.markVisibleAsRead();
      }, 1000);

      // Scroll para notificação específica
      if (notificationId) {
        this.$nextTick(() => {
          const element = document.querySelector(`[data-notification-id="${notificationId}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    },

    closePanel() {
      this.isOpen = false;
      this.highlightedNotificationId = null;
    },

    togglePanel() {
      if (this.isOpen) {
        this.closePanel();
      } else {
        this.openPanel();
      }
    },

    handleKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.closePanel();
      }
    },

    markVisibleAsRead() {
      let hasChanges = false;
      this.notifications.forEach((notification, index) => {
        if (!notification.read) {
          this.$set(this.notifications[index], 'read', true);
          hasChanges = true;
        }
      });

      if (hasChanges) {
        this.saveNotifications();
        this.$root.$emit('notifications-read');
      }
    },

    markAllAsRead() {

      let hasChanges = false;

      this.notifications.forEach((n, index) => {
        if (!n.read) {
          this.$set(this.notifications[index], 'read', true);
          hasChanges = true;
        }
      });

      if (hasChanges) {
        // Salvar no localStorage
        this.saveNotifications();

        // Aguardar um tick para garantir que o localStorage foi atualizado
        this.$nextTick(() => {
          this.$root.$emit('notifications-read');
          this.$root.$emit('notifications-updated');
        });

        Message.success('Todas as notificações foram marcadas como lidas');
      } else {
        Message.info('Todas as notificações já estão lidas');
      }
    },

    confirmClearAll() {
      this.showClearConfirmation = true;
    },

    cancelClearAll() {
      this.showClearConfirmation = false;
    },

    executeClearAll() {

      // Esconder confirmação
      this.showClearConfirmation = false;

      // Limpar array local primeiro
      this.notifications = [];

      // Limpar localStorage via NotificationService
      NotificationService.clearAll();

      // Aguardar um tick para garantir que o localStorage foi limpo
      this.$nextTick(() => {
        this.$root.$emit('notifications-updated');
        this.$root.$emit('notifications-read');
      });

      // Feedback
      Message({
        message: this.$t('mensagens.notificacoes.todasRemovidas'),
        type: 'success',
        duration: 3000
      });
    },

    removeNotification(notificationId) {
      NotificationService.removeNotification(notificationId);
      const index = this.notifications.findIndex(n => n.id === notificationId);
      if (index > -1) {
        this.notifications.splice(index, 1);
      }
    },

    handleNotificationClick(notification) {
      // Marca como lida
      if (!notification.read) {
        const index = this.notifications.findIndex(n => n.id === notification.id);
        if (index > -1) {
          this.$set(this.notifications[index], 'read', true);
          this.saveNotifications();
          this.$root.$emit('notifications-read');
        }
      }

      // Se tiver apenas uma ação, executa diretamente
      if (notification.actions && notification.actions.length === 1) {
        this.handleAction(notification.actions[0], notification);
      }
    },

    handleAction(action, notification) {
      if (action.handler && typeof action.handler === 'function') {
        action.handler(this, notification);
      }
    },

    getCategoryCount(category) {
      return this.notifications.filter(n => n.category === category).length;
    },

    getNotificationVariant(status) {
      const statusMap = {
        'SUCCESS': 'success',
        'PROCESSADO': 'success',
        'ERROR': 'error',
        'ERRO': 'error',
        'WARNING': 'warning',
        'ALERTA': 'warning',
        'INFO': 'info',
        'EM_PROCESSAMENTO': 'info',
        'SOLICITADO': 'info'
      };
      return statusMap[status] || 'info';
    },

    getNotificationIcon(status) {
      const iconMap = {
        'SUCCESS': 'el-icon-success',
        'PROCESSADO': 'el-icon-success',
        'ERROR': 'el-icon-error',
        'ERRO': 'el-icon-error',
        'WARNING': 'el-icon-warning',
        'ALERTA': 'el-icon-warning',
        'INFO': 'el-icon-info',
        'EM_PROCESSAMENTO': 'el-icon-loading',
        'SOLICITADO': 'el-icon-info'
      };
      return iconMap[status] || 'el-icon-info';
    },

    formatTime(timestamp) {
      if (!timestamp) return '';

      const now = Date.now();
      const diff = now - timestamp;

      // Menos de 1 minuto
      if (diff < 60000) {
        return this.$t('mensagens.notificacoes.agora');
      }

      // Menos de 1 hora
      if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000);
        return this.$t('mensagens.notificacoes.minutosAtras', { minutos: minutes });
      }

      // Menos de 24 horas
      if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        return this.$t('mensagens.notificacoes.horasAtras', { horas: hours });
      }

      // Mais de 24 horas
      const date = new Date(timestamp);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
  }
};
</script>

<style scoped lang="scss">
/* Overlay */
.notification-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-fade-enter,
.overlay-fade-leave-to {
  opacity: 0;
}

/* Side Panel */
.notification-sidepanel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 480px;
  background: white;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: transform 0.3s ease;
}

.panel-slide-enter,
.panel-slide-leave-to {
  transform: translateX(100%);
}

/* Header */
.panel-header {
  padding: 20px 24px;
  border-bottom: 1px solid #EBEEF5;
  background: #F5F7FA;
}

.panel-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  i {
    font-size: 20px;
    color: #409EFF;
  }

  .unread-badge {
    margin-left: 4px;
  }
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.close-button {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #909399;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin-left: auto;

  &:hover {
    background: #E4E7ED;
    color: #303133;
  }

  i {
    font-size: 20px;
  }
}

/* Filtros */
.panel-filters {
  padding: 16px 24px;
  border-bottom: 1px solid #EBEEF5;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  background: white;
}

/* Content */
.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #909399;

  i {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
}

/* Notification Item */
.notification-item {
  padding: 16px 24px;
  border-bottom: 1px solid #EBEEF5;
  display: flex;
  gap: 12px;
  position: relative;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #F5F7FA;
  }

  &.unread {
    background: #EDF2FC;

    &:hover {
      background: #E1EAFC;
    }
  }
}

.unread-indicator {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #409EFF;
}

.notification-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;

  &.icon-success {
    background: #F0F9FF;
    color: #67C23A;
  }

  &.icon-error {
    background: #FEF0F0;
    color: #F56C6C;
  }

  &.icon-warning {
    background: #FDF6EC;
    color: #E6A23C;
  }

  &.icon-info {
    background: #F4F4F5;
    color: #409EFF;
  }
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 4px;
}

.notification-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}

.notification-time {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
}

.notification-message {
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  margin-bottom: 8px;
  word-break: break-word;
}

.notification-metadata {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;

  i {
    font-size: 14px;
  }
}

.notification-category {
  margin-bottom: 8px;
}

.notification-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.remove-button {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #C0C4CC;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  align-self: flex-start;

  &:hover {
    background: #F5F7FA;
    color: #606266;
  }

  i {
    font-size: 16px;
  }
}

/* Modal de Confirmação Inline */
.clear-confirmation {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 20px;
}

.confirmation-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 360px;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  text-align: center;

  i {
    font-size: 48px;
    color: #E6A23C;
    margin-bottom: 16px;
  }
}

.confirmation-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
}

.confirmation-message {
  font-size: 14px;
  color: #606266;
  margin: 0 0 20px 0;
}

.confirmation-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

/* Transição para a confirmação */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Responsividade */
@media (max-width: 768px) {
  .notification-sidepanel {
    width: 100%;
  }

  .confirmation-content {
    max-width: 280px;
  }
}

/* Scrollbar customizado */
.panel-content::-webkit-scrollbar {
  width: 6px;
}

.panel-content::-webkit-scrollbar-track {
  background: #F5F7FA;
}

.panel-content::-webkit-scrollbar-thumb {
  background: #DCDFE6;
  border-radius: 3px;

  &:hover {
    background: #C0C4CC;
  }
}
</style>
